import * as Utils from "../Utils";
import { adminFetch, buildQueryString } from "./admin_api";
import { formatCurrency, normalizePagination } from "./affiliate_helper";

const getAuthToken = () => localStorage.getItem("adminToken");

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
});

const offerApi = (path, options = {}) =>
  adminFetch(`${Utils.API_URL}v1/admin${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

/** POST /api/v1/admin/platform-promo-codes */
export async function createPlatformPromoCode(payload) {
  return offerApi("/platform-promo-codes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** GET /api/v1/admin/platform-promo-codes */
export async function getPlatformPromoCodes(params = {}) {
  return offerApi(`/platform-promo-codes${buildQueryString(params)}`, { method: "GET" });
}

/** GET /api/v1/admin/platform-promo-codes/:id */
export async function getPlatformPromoCodeDetails(id) {
  return offerApi(`/platform-promo-codes/${id}`, { method: "GET" });
}

/** PATCH /api/v1/admin/platform-promo-codes/:id */
export async function updatePlatformPromoCode(id, payload) {
  return offerApi(`/platform-promo-codes/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** DELETE /api/v1/admin/platform-promo-codes/:id */
export async function deletePlatformPromoCode(id) {
  return offerApi(`/platform-promo-codes/${id}`, { method: "DELETE" });
}

/** POST /api/v1/admin/offers */
export async function createPromotionalOffer(payload) {
  return offerApi("/offers", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** GET /api/v1/admin/offers */
export async function getPromotionalOffers(params = {}) {
  return offerApi(`/offers${buildQueryString(params)}`, { method: "GET" });
}

/** GET /api/v1/admin/offers/:id */
export async function getPromotionalOfferDetails(id) {
  return offerApi(`/offers/${id}`, { method: "GET" });
}

/** PATCH /api/v1/admin/offers/:id */
export async function updatePromotionalOffer(id, payload) {
  return offerApi(`/offers/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** PATCH /api/v1/admin/offers/:id/status */
export async function updatePromotionalOfferStatus(id, status) {
  return offerApi(`/offers/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

/** DELETE /api/v1/admin/offers/:id */
export async function deletePromotionalOffer(id) {
  return offerApi(`/offers/${id}`, { method: "DELETE" });
}

/** GET /api/v1/admin/offers/analytics */
export async function getOfferAnalytics(params = {}) {
  return offerApi(`/offers/analytics${buildQueryString(params)}`, { method: "GET" });
}

/** GET /api/v1/admin/offers/:id/analytics */
export async function getOfferAnalyticsById(id, params = {}) {
  return offerApi(`/offers/${id}/analytics${buildQueryString(params)}`, { method: "GET" });
}

export const PROMO_DISCOUNT_TYPES = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed_amount", label: "Fixed amount" },
  { value: "free_shipping", label: "Free shipping" },
];

export const OFFER_TYPES = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed_amount", label: "Fixed amount" },
  { value: "free_shipping", label: "Free shipping" },
  { value: "referral_discount", label: "Referral discount" },
  { value: "festival", label: "Festival" },
  { value: "seasonal", label: "Seasonal" },
];

export const OFFER_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const CODE_SOURCES = [
  { value: "platform", label: "Platform promo" },
  { value: "referral", label: "Referral discount" },
];

export const getOfferStatusBadge = (status) => {
  switch (status) {
    case "active":
      return "success";
    case "inactive":
      return "secondary";
    default:
      return "secondary";
  }
};

export const normalizePromoCodeList = (res) => {
  const candidates = [res?.data?.items, res?.items, res?.data];
  const list = candidates.find((item) => Array.isArray(item));
  return list || [];
};

export const normalizePromoCodeDetail = (res) => {
  if (!res) return null;
  if (res?.data && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
};

export const normalizeOfferList = (res) => {
  const candidates = [res?.data?.items, res?.items, res?.data];
  const list = candidates.find((item) => Array.isArray(item));
  return list || [];
};

export const normalizeOfferDetail = (res) => {
  if (!res) return null;
  if (res?.data && typeof res.data === "object" && !Array.isArray(res.data)) {
    return res.data;
  }
  return res;
};

export const normalizeOfferPagination = (res) => normalizePagination(res);

export const normalizeOfferAnalytics = (res) => {
  const data = res?.data || res || {};
  return {
    summary: data.summary || {
      totalOffers: 0,
      activeOffers: 0,
      totalViews: 0,
      totalClicks: 0,
      totalCopies: 0,
      totalCouponUsage: 0,
      totalDiscountGiven: 0,
      clickThroughRate: 0,
      conversionRate: 0,
      currency: "INR",
    },
    topOffers: Array.isArray(data.topOffers) ? data.topOffers : [],
    monthlyReports: Array.isArray(data.monthlyReports) ? data.monthlyReports : [],
    offers: Array.isArray(data.offers) ? data.offers : [],
  };
};

export const formatDiscountDisplay = (discountType, discountValue, currency = "INR") => {
  switch (discountType) {
    case "percentage":
      return `${discountValue ?? 0}%`;
    case "fixed_amount":
      return formatCurrency(discountValue, currency);
    case "free_shipping":
      return "Free shipping";
    default:
      return "—";
  }
};

export const formatUsageDisplay = (current, max) => {
  const used = Number(current) || 0;
  if (max === null || max === undefined || max === "") {
    return `${used} / unlimited`;
  }
  const limit = Number(max);
  if (Number.isNaN(limit)) return `${used}`;
  return `${used} / ${limit}`;
};

export const getRowId = (row) => row?.id || row?._id || null;

export { formatCurrency };
